import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sprout, Shield, Zap, Truck, Coffee } from 'lucide-react';

const ProdutosServicos = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const services = [
    {
      icon: Sprout,
      title: 'Sementes',
      description: 'Sementes de alta qualidade certificadas para garantir a melhor germinação e produtividade em sua lavoura.',
      color: 'green',
      image: 'https://customer-assets.emergentagent.com/job_8b43b00b-d49b-410d-b712-afa38bbd140c/artifacts/7l3fjgd6_Design%20sem%20nome%20%2828%29.jpg',
    },
    {
      icon: Shield,
      title: 'Defensivos',
      description: 'Linha completa de defensivos agrícolas para proteção eficaz contra pragas, doenças e plantas daninhas.',
      color: 'blue',
      image: 'https://customer-assets.emergentagent.com/job_8b43b00b-d49b-410d-b712-afa38bbd140c/artifacts/ym5gji3o_193914-empilhadeiras-e-corredores-voce-sabe-qual-a-relacao-entre-os-dois-.jpg',
    },
    {
      icon: Zap,
      title: 'Fertilizantes',
      description: 'Fertilizantes de última geração para nutrição balanceada e aumento da produtividade da sua cultura.',
      color: 'yellow',
      image: 'https://customer-assets.emergentagent.com/job_8b43b00b-d49b-410d-b712-afa38bbd140c/artifacts/1wmqjrox_Fertilizantes.jpeg',
    },
    {
      icon: Coffee,
      title: 'Café',
      description: 'Cultivar Café — comercialização e parceria estratégica com cafeicultores, garantindo qualidade do grão à entrega.',
      color: 'amber',
      image: 'https://customer-assets.emergentagent.com/job_f1f17df6-0a32-4d0a-b237-628c328d9963/artifacts/flbmj315_IMG_3779.JPG',
    },
    {
      icon: Truck,
      title: 'Transporte e Logística',
      description: 'Soluções completas de transporte com a Cultivar Log. Frota própria para atender sua safra com agilidade.',
      color: 'purple',
      image: 'https://customer-assets.emergentagent.com/job_primeiras-paginas/artifacts/gc3odst1_Sem%20t%C3%ADtulo-1log.png',
      imageMode: 'logo',
    },
  ];

  const colorClasses = {
    green: {
      bg: 'bg-green-100',
      icon: 'text-green-600',
      button: 'bg-green-600 hover:bg-green-700',
      border: 'border-green-200',
    },
    blue: {
      bg: 'bg-blue-100',
      icon: 'text-blue-600',
      button: 'bg-blue-600 hover:bg-blue-700',
      border: 'border-blue-200',
    },
    yellow: {
      bg: 'bg-yellow-100',
      icon: 'text-yellow-600',
      button: 'bg-yellow-600 hover:bg-yellow-700',
      border: 'border-yellow-200',
    },
    amber: {
      bg: 'bg-amber-100',
      icon: 'text-amber-700',
      button: 'bg-amber-700 hover:bg-amber-800',
      border: 'border-amber-200',
    },
    purple: {
      bg: 'bg-purple-100',
      icon: 'text-purple-600',
      button: 'bg-purple-600 hover:bg-purple-700',
      border: 'border-purple-200',
    },
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-r from-green-600 to-green-700 text-white py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-6xl font-bold mb-4 md:mb-6">PRODUTOS E SERVIÇOS</h1>
          <p className="text-base md:text-2xl max-w-4xl mx-auto">
            Na Cultivar, fornecemos tudo o que sua lavoura precisa para crescer forte e produtiva: defensivos, adubos e sementes de qualidade. Parceiro do produtor do início ao fim da safra!
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
            {services.map((service, index) => {
              const Icon = service.icon;
              const colors = colorClasses[service.color];
              
              return (
                <div
                  key={index}
                  className={`bg-white rounded-xl shadow-lg overflow-hidden border-4 ${colors.border} hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2`}
                >
                  {service.imageMode === 'logo' ? (
                    <div className="h-48 flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-6">
                      <img src={service.image} alt={service.title} className="max-h-full max-w-full object-contain drop-shadow-lg" />
                    </div>
                  ) : (
                    <div
                      className="h-48 bg-cover bg-center"
                      style={{ backgroundImage: `url(${service.image})` }}
                    ></div>
                  )}
                  <div className="p-8">
                    <div className="flex items-center mb-4">
                      <div className={`${colors.bg} p-4 rounded-full`}>
                        <Icon className={`w-8 h-8 ${colors.icon}`} />
                      </div>
                      <h3 className="text-2xl font-bold ml-4 text-gray-800">{service.title}</h3>
                    </div>
                    <p className="text-gray-600 text-lg mb-6 leading-relaxed">{service.description}</p>
                    <Link
                      to={
                        service.title === 'Transporte e Logística'
                          ? '/cultivar-log'
                          : service.title === 'Café'
                          ? '/cultivar-cafe'
                          : '/produtos'
                      }
                      className={`inline-block ${colors.button} text-white font-semibold px-6 py-3 rounded-lg transition-colors`}
                    >
                      {service.title === 'Transporte e Logística'
                        ? 'SAIBA MAIS'
                        : service.title === 'Café'
                        ? 'SAIBA MAIS'
                        : 'VER CATÁLOGO'}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Agricultura de Precisão - Calcário */}
      <section id="agricultura-precisao" className="py-12 md:py-20 bg-gradient-to-r from-blue-900 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">AGRICULTURA DE PRECISÃO</h2>
            <p className="text-lg md:text-2xl text-green-300">Aplicação de Calcário com Tecnologia de Ponta</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center mb-12">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-6">Tecnologia que maximiza sua produtividade</h3>
              <p className="text-base md:text-lg mb-6 leading-relaxed">
                Nossa frota de caminhões equipados com sistema de GPS de alta precisão garante a aplicação uniforme de calcário em toda a sua lavoura, otimizando o uso de insumos e aumentando a produtividade.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 text-2xl">✓</span>
                  <span>Sistema GPS com precisão centimétrica</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 text-2xl">✓</span>
                  <span>Aplicação em taxa variável conforme necessidade do solo</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 text-2xl">✓</span>
                  <span>Relatórios detalhados de cada aplicação</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 text-2xl">✓</span>
                  <span>Equipe especializada e frota moderna</span>
                </li>
              </ul>
              <Link
                to="/contato"
                className="inline-block mt-8 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg"
              >
                SOLICITAR ORÇAMENTO
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_8b43b00b-d49b-410d-b712-afa38bbd140c/artifacts/pa7qx0lc_IMG_0565.JPG" 
                alt="Caminhão aplicando calcário" 
                className="rounded-xl shadow-2xl w-full h-40 md:h-52 object-cover"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_8b43b00b-d49b-410d-b712-afa38bbd140c/artifacts/zturgeyd_IMG_0320.JPG" 
                alt="Sistema GPS de precisão" 
                className="rounded-xl shadow-2xl w-full h-40 md:h-52 object-cover"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_f1f17df6-0a32-4d0a-b237-628c328d9963/artifacts/89uodwcf_IMG_0307.JPG" 
                alt="Caminhão Cultivar Constellation com aplicador" 
                className="rounded-xl shadow-2xl w-full h-40 md:h-52 object-cover"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_8b43b00b-d49b-410d-b712-afa38bbd140c/artifacts/smnkzn0x_IMG_0306.JPG" 
                alt="Carregamento de calcário" 
                className="rounded-xl shadow-2xl w-full h-40 md:h-52 object-cover"
              />
            </div>
          </div>

          {/* Estatísticas */}
          <div className="grid grid-cols-3 gap-4 md:gap-8 mt-8">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 md:p-6 text-center">
              <div className="text-3xl md:text-5xl font-bold text-green-400 mb-2">GPS</div>
              <p className="text-sm md:text-base">Precisão Centimétrica</p>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 md:p-6 text-center">
              <div className="text-3xl md:text-5xl font-bold text-green-400 mb-2">24h</div>
              <p className="text-sm md:text-base">Operação Contínua</p>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 md:p-6 text-center">
              <div className="text-3xl md:text-5xl font-bold text-green-400 mb-2">100%</div>
              <p className="text-sm md:text-base">Cobertura Uniforme</p>
            </div>
          </div>
        </div>
      </section>

      {/* Catálogo de Produtos */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-4 md:mb-6">Conheça nosso catálogo de produtos</h2>
          <p className="text-gray-600 text-base md:text-lg mb-6 md:mb-8 max-w-2xl mx-auto">
            Explore nossa linha completa de produtos agrícolas com informações técnicas detalhadas.
          </p>
          <Link
            to="/produtos"
            className="inline-block bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 md:py-4 md:px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            VER CATÁLOGO COMPLETO
          </Link>
        </div>
      </section>

      {/* Nossas Marcas - Cultivar Log */}
      <section className="py-12 md:py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-800 text-center mb-4">Nossas Marcas</h2>
          <p className="text-gray-600 text-base md:text-lg text-center mb-8 md:mb-12 max-w-2xl mx-auto">
            Conheça também as outras empresas do nosso grupo
          </p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12">
            <Link 
              to="/cultivar-log" 
              className="bg-white rounded-2xl shadow-lg p-4 md:p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_primeiras-paginas/artifacts/gc3odst1_Sem%20t%C3%ADtulo-1log.png" 
                alt="Cultivar Log" 
                className="h-16 md:h-24 object-contain"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-20 bg-blue-600 text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">Precisa de ajuda para escolher?</h2>
          <p className="text-base md:text-xl mb-6 md:mb-8">Nossa equipe de especialistas está pronta para ajudar você a encontrar a melhor solução</p>
          <Link
            to="/contato"
            className="inline-block bg-white text-blue-600 hover:bg-gray-100 font-bold py-3 px-6 md:py-4 md:px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            FALE COM UM ESPECIALISTA
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProdutosServicos;